import React, { createContext, useContext, useEffect, useState } from 'react';
import userManager from './AuthConfig';
import GA from '../services/GoogleAnalytics';

interface AuthContextType {
  user: User | null;
  login: (username: string, password: string, token: string) => Promise<User>;
  loginWithOtp: (username: string, accessToken: string, expiresIn?: string, userData?: any) => Promise<User>;
  logout: () => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

export interface UserProfile {
  name: string;
  email: string;
  username: string;
  fullname: string;
  phoneNumber: string;
  address_postalcode: string;
  address_street: string;
  address_district: string;
  address_number: string;
  address_city: string;
  address_state: string;
  address_complement: string;
  address_reference: string;
}

export interface User {
  profile: UserProfile;
  access_token: string;
  id_token: string;
  expires_in: string;
  status?: string;
  updated_at?: string;
  expiration_time?: number;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({children}) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  
  useEffect(() => {
    const initAuth = async () => {
      const currentUser = await userManager.getUser();
      setUser(currentUser);
      setIsLoading(false);
    };
    
    initAuth();
  }, []);
  
  const login = async (username: string, password: string, token: string): Promise<User> => {
    try {
      const loggedUser = await userManager.login(username, password, token);
      setUser(loggedUser);
      
      // Evento GA4: Login realizado com sucesso
      if (GA.initialized) {
        GA.events.login('password');
      }
      
      return loggedUser;
    } catch (error) {
      // Evento GA4: Erro no login
      if (GA.initialized) {
        GA.events.error('auth_login_error', error instanceof Error ? error.message : 'Erro no login');
      }
      throw error;
    }
  };
  
  // Nova função para login via OTP
  const loginWithOtp = async (username: string, accessToken: string, expiresIn?: string, userData?: any): Promise<User> => {
    try {
      let loggedUser: User;
      
      // Se userData for fornecido, usa o método direto
      if (userData) {
        loggedUser = await userManager.loginOtpWithUserData(username, accessToken, expiresIn || '3600', userData);
      } else {
        // Senão, usa o método tradicional que busca os dados via API
        loggedUser = await userManager.loginOtp(username, accessToken, expiresIn || '3600');
      }
      
      setUser(loggedUser);
      
      // Evento GA4: Login via OTP realizado com sucesso
      if (GA.initialized) {
        GA.events.login('otp');
      }
      
      return loggedUser;
      
    } catch (error) {
      console.error('Erro no login via OTP:', error);
      
      // Evento GA4: Erro no login via OTP
      if (GA.initialized) {
        GA.events.error('auth_otp_login_error', error instanceof Error ? error.message : 'Erro no login via OTP');
      }
      
      throw error;
    }
  };
  
  const logout = async (): Promise<void> => {
    // Evento GA4: Logout do usuário
    if (GA.initialized) {
      GA.events.logout();
    }
    
    await userManager.signoutRedirect();
    setUser(null);
  };
  
  if (isLoading) {
    return null;
  }
  
  return (
    <AuthContext.Provider value={{user, login, loginWithOtp, logout, isLoading, error: null}}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  
  return context;
};
